import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"
import { ICreateShiftPayload, IUpdateShiftPausePayload, IUpdateShiftResponse, IUpdateShiftWorkingHoursPayload, ShiftsResponse } from "../../../types"
import { httpClient } from "../network"
import { createShiftEndpoint, deleteShiftEndpoint, getShiftsEndpoint, updateShiftPauseEndpoint, updateShiftWorkingHoursEndpoint } from "../endpoints/shiftEndpoints"
import { toast } from "react-toastify"


export const useCreateShift = (): UseMutationResult<any, unknown, ICreateShiftPayload, unknown> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ICreateShiftPayload) => {
            if(payload.pauseStart === '')
                payload.pauseStart = '00:00';

            if(payload.pauseEnd === '')
                payload.pauseEnd = '00:00';
            
            const response = await httpClient.post(createShiftEndpoint, payload);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['getAllShifts']})
            queryClient.invalidateQueries({queryKey: ['shiftPlans']})
            queryClient.invalidateQueries({queryKey: ['getAllServices']})
            toast.success("Smjena je uspješno kreirana");
          },
        onError: () => {
            toast.error("Smjena nije uspješno kreirana")
        },
    })
}

export const useGetAllShifts = (): UseQueryResult<ShiftsResponse, unknown> => {
    return useQuery({
        queryKey: ["getAllShifts"],
        queryFn: async () => {
            const response = await httpClient.get(getShiftsEndpoint);
            return response.data;
        },
        retry: 0,
    })
}

export const useDeleteShift = (): UseMutationResult<any, unknown, string, unknown> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const response = await httpClient.delete(deleteShiftEndpoint(id))
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getAllShifts"] });
            queryClient.invalidateQueries({ queryKey: ["shiftPlans"] });
            queryClient.invalidateQueries({ queryKey: ["getAllServices"] });
            queryClient.invalidateQueries({ queryKey: ["getAllSchedules"] });
        },
        onError: () => {
            toast.error("Smjena nije uspješno obrisana");
        }
    })
}

export const useUpdateShiftWorkingHours = (): UseMutationResult<IUpdateShiftResponse, unknown, { id: string; payload: IUpdateShiftWorkingHoursPayload }, unknown> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: IUpdateShiftWorkingHoursPayload }) => {
            const response = await httpClient.put(updateShiftWorkingHoursEndpoint(id), payload);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["getAllShifts"] });
            queryClient.invalidateQueries({ queryKey: ["getAllSchedules"] });
            queryClient.invalidateQueries({ queryKey: ["shiftPlans"] });
            queryClient.invalidateQueries({ queryKey: ["getAllServices"] });
            
            if (data.data.unchangedDates.length > 0) {
                toast.info(
                    `Radno vrijeme uspješno promijenjeno. ${data.data.unchangedSchedulesCount} termina nije izmijenjeno zbog postojećih rezervacija.`,
                    { autoClose: 5000 }
                );
            } else {
                toast.success("Radno vrijeme uspješno promijenjeno");
            }
        },
        onError: () => {
            toast.error("Greška prilikom ažuriranja radnog vremena");
        }
    })
}

export const useUpdateShiftPause = (): UseMutationResult<any, unknown, { id: string; payload: IUpdateShiftPausePayload }, unknown> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: IUpdateShiftPausePayload }) => {
            const response = await httpClient.put(updateShiftPauseEndpoint(id), payload);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getAllShifts"] });
            queryClient.invalidateQueries({ queryKey: ["getAllSchedules"] });
            toast.success("Pauza uspješno uređena");
        },
        onError: () => {
            toast.error("Greška prilikom uređivanja pauze");
        }
    })
}